import { useMutation } from '@tanstack/react-query';
import type { QuoteDTO } from '@setvi/shared/services/react-query';
import { extractItems, buildQuoteRequestBody } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';
import { useQueuedUpdateMutation } from './use-queued-update-mutation';

export interface QuoteMutationConfig<TVariables> {
  /** Quote ID for cache operations */
  quoteId: string;

  /**
   * Optional optimistic update function
   * If provided, UI updates immediately before API call
   * The updated cache is then used for the API call
   */
  optimisticUpdate?: (
    quote: QuoteDTO | undefined,
    variables: TVariables
  ) => QuoteDTO | undefined;

  /**
   * Custom error handler
   */
  onError?: (error: unknown, variables: TVariables) => void;

  /**
   * Custom success handler
   */
  onSuccess?: (data: unknown, variables: TVariables) => void;
}

/**
 * Factory hook for creating quote mutations with standardized behavior:
 * - Automatic cache management
 * - Optimistic updates with rollback on error
 * - Automatic query invalidation on success
 *
 * @example
 * ```ts
 * const removeItem = useQuoteMutation({
 *   quoteId,
 *   optimisticUpdate: (quote, itemId) => removeItemOptimistically(quote, itemId),
 * });
 * ```
 */
export const useQuoteMutation = <TVariables>(
  config: QuoteMutationConfig<TVariables>
) => {
  const {
    quoteId,
    optimisticUpdate,
    onError: customOnError,
    onSuccess: customOnSuccess
  } = config;

  const cache = useQuoteCache(quoteId);
  const {
    mutateAsync: updateQuote,
    hasQueuedMutations,
    setOnQueueEmpty
  } = useQueuedUpdateMutation();

  return useMutation({
    mutationFn: async () => {
      await cache.cancel();

      // Get current quote from cache (already updated by onMutate if optimisticUpdate provided)
      const currentQuote = cache.getQuote();

      if (!currentQuote) {
        throw new Error('Quote not found in cache');
      }

      // Extract items for API (remove UI-only fields)
      const items = extractItems(currentQuote.items);

      // Send complete quote data
      return updateQuote({
        quoteId,
        body: buildQuoteRequestBody(currentQuote, items)
      });
    },

    onMutate: optimisticUpdate
      ? async (variables: TVariables) => {
          await cache.cancel();

          // Snapshot previous value for rollback
          const previousQuote = cache.getQuote();

          // Optimistically update cache for immediate UI feedback
          cache.setQuote(old => optimisticUpdate(old, variables));

          return { previousQuote };
        }
      : undefined,

    onError: (error, variables, context) => {
      // Rollback to previous value on error (if optimistic update was used)
      if (optimisticUpdate && context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }

      if (customOnError) {
        customOnError(error, variables);
      }
    },

    onSuccess: (data, variables) => {
      // Skip invalidate if there are queued mutations to preserve optimistic updates
      if (hasQueuedMutations()) {
        // Defer invalidate until all queued mutations complete
        setOnQueueEmpty(() => cache.invalidate());
      } else {
        // No queued mutations, invalidate immediately
        cache.invalidate();
      }

      if (customOnSuccess) {
        customOnSuccess(data, variables);
      }
    }
  });
};
